/**
 * Форматирование дат и чисел для интерфейса.
 *
 * В базе всё хранится в UTC; здесь абсолютное время переводится в часовой пояс
 * отображения. Относительные подписи («вчера», «3 дня назад») читаются быстрее
 * точных дат в лентах и списках, но точная дата всегда доступна в подсказке.
 */

import { DISPLAY_TIMEZONE, daysUntilDay } from '@project-os/contracts';

// Ключ дня переэкспортируется: страницы уже импортируют его отсюда. Правило
// просрочки — нет: его берут прямо из общего пакета, чтобы не заводить второй
// путь к одной и той же функции.
export { dayKey } from '@project-os/contracts';

const LOCALE = 'ru-RU';

/** Формат даты в интерфейсе. Выбирается в настройках отображения. */
export type DateStyle = 'dd.mm.yyyy' | 'iso' | 'long';

const dateFormatters: Record<DateStyle, Intl.DateTimeFormat> = {
  // Российский формат: цифрами, с ведущими нулями — колонка дат выравнивается.
  'dd.mm.yyyy': new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: DISPLAY_TIMEZONE,
  }),
  // ISO: сортируется как текст, поэтому удобен при выгрузке и сверке.
  iso: new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: DISPLAY_TIMEZONE,
  }),
  // Словами: там, где дата стоит в предложении, а не в колонке.
  long: new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: DISPLAY_TIMEZONE,
  }),
};

/**
 * Текущий формат дат.
 *
 * Модуль не зависит от React, поэтому настройка кладётся сюда провайдером —
 * это единственное глобальное состояние в файле, и оно осознанное: иначе
 * каждый вызов formatDate пришлось бы протаскивать через пропсы.
 */
let activeDateStyle: DateStyle = 'dd.mm.yyyy';

export function setActiveDateStyle(style: DateStyle): void {
  activeDateStyle = style;
}

const dateFormatter = {
  format: (date: Date) => dateFormatters[activeDateStyle].format(date),
};

const dateTimeFormatter = new Intl.DateTimeFormat(LOCALE, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: DISPLAY_TIMEZONE,
});

const relativeFormatter = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? '—' : dateFormatter.format(date);
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? '—' : dateTimeFormatter.format(date);
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['week', 7 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
];

export function formatRelative(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';

  const diff = date.getTime() - Date.now();
  const absolute = Math.abs(diff);

  if (absolute < 60 * 1000) return 'только что';

  for (const [unit, ms] of RELATIVE_UNITS) {
    if (absolute >= ms) {
      return relativeFormatter.format(Math.round(diff / ms), unit);
    }
  }
  return 'только что';
}

/** Сколько дней прошло с указанной даты. Для возраста блокеров. */
export function daysSince(value: string | null | undefined): number | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Math.floor((Date.now() - date.getTime()) / (24 * 60 * 60 * 1000));
}

/** Инициалы для аватара: «Анна Ковалёва» → «АК». */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Русское склонение существительного по числу.
 *
 * Три формы: для 1, для 2–4, для 5 и далее. Возвращается только слово —
 * число подставляет вызывающий, когда оно вообще нужно.
 *
 *   pluralWordRu(1, ['задача', 'задачи', 'задач'])  → 'задача'
 *   pluralWordRu(3, ['задача', 'задачи', 'задач'])  → 'задачи'
 *   pluralWordRu(5, ['задача', 'задачи', 'задач'])  → 'задач'
 */
export function pluralWordRu(count: number, forms: [string, string, string]): string {
  const mod10 = Math.abs(count) % 10;
  const mod100 = Math.abs(count) % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
  return forms[2];
}

/**
 * Число вместе со склонённым словом: «1 задача», «2 задачи», «5 задач».
 */
export function pluralRu(count: number, forms: [string, string, string]): string {
  return `${count} ${pluralWordRu(count, forms)}`;
}

/**
 * Календарная дата словами: «сегодня», «вчера», «3 дня назад», «через 2 дня».
 *
 * Отличается от formatRelative тем, что считает разницу в календарных днях,
 * а не в миллисекундах: событие вчера в 23:50 и сегодня в 00:10 разделяют
 * двадцать минут, но это разные дни, и человек назовёт их по-разному.
 *
 * За пределами двух недель возвращается обычная дата: «17 дней назад» уже
 * ничего не сообщает, а «12 авг. 2026 г.» сообщает.
 */
export function formatDayRelative(value: string | Date | null | undefined): string {
  const days = calendarDaysFromToday(value);
  if (days === null) return '—';

  if (days === 0) return 'сегодня';
  if (days === 1) return 'вчера';
  if (days === -1) return 'завтра';
  if (days === 2) return 'позавчера';

  if (days > 0 && days <= 14) return `${pluralRu(days, ['день', 'дня', 'дней'])} назад`;
  if (days < 0 && days >= -14) {
    return `через ${pluralRu(-days, ['день', 'дня', 'дней'])}`;
  }

  return formatDate(value);
}

/**
 * Разница в календарных днях от сегодняшнего дня в часовом поясе отображения.
 * Положительное число — прошлое, отрицательное — будущее.
 *
 * Знак противоположен `daysUntilDay`: здесь считают «сколько прошло», там —
 * «сколько осталось». Обе величины выводятся из одного счёта дней, поэтому
 * разойтись не могут.
 */
export function calendarDaysFromToday(value: string | Date | null | undefined): number | null {
  if (!value) return null;
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return null;

  return -daysUntilDay(date);
}

/**
 * Возраст записи словами: «2 дня», «3 недели», «5 месяцев».
 *
 * Нужен в таблицах, где важно не «когда создано», а «сколько уже лежит».
 */
export function formatAge(value: string | Date | null | undefined): string {
  const days = calendarDaysFromToday(value);
  if (days === null) return '—';
  if (days <= 0) return 'сегодня';
  if (days < 7) return pluralRu(days, ['день', 'дня', 'дней']);
  if (days < 31) return pluralRu(Math.floor(days / 7), ['неделя', 'недели', 'недель']);
  if (days < 365) return pluralRu(Math.floor(days / 30), ['месяц', 'месяца', 'месяцев']);
  return pluralRu(Math.floor(days / 365), ['год', 'года', 'лет']);
}
